import { CHAT_API_BASE_URL } from "./chatService";
import { authedFetch } from "../auth/authedFetch";
import type { RedditPost } from "../context/RedditFeedContext";

/**
 * The Replied tab's data source — separate from RedditFeedContext because
 * that context owns the *live* board (agent-scoped, held in memory across
 * navigation so a 30-100s build survives the user leaving the chat window).
 * Replied history has none of those needs: it's a plain paginated read, so a
 * small stateless function is enough rather than another provider.
 */
export interface RepliedPostsResult {
  posts: RedditPost[];
  nextCursor: string | null;
}

export async function fetchRepliedPosts(params: {
  customerId: string;
  agentId: string;
  idToken: string;
  startDate?: string;
  endDate?: string;
  cursor?: string | null;
  limit?: number;
}): Promise<RepliedPostsResult> {
  const base = (CHAT_API_BASE_URL || "").replace(/\/$/, "");
  const resp = await authedFetch(
    `${base}/reddit/replied`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        customer_id: params.customerId,
        agent_id: params.agentId,
        start_date: params.startDate || undefined,
        end_date: params.endDate || undefined,
        cursor: params.cursor || undefined,
        limit: params.limit ?? 25,
      }),
    },
    params.idToken
  );

  if (!resp.ok) {
    const detail = await resp
      .json()
      .then((body) => (typeof body?.detail === "string" ? body.detail : null))
      .catch(() => null);
    throw new Error(detail || `Couldn't load replied posts (HTTP ${resp.status}).`);
  }

  const data = await resp.json();
  return {
    posts: data.posts || [],
    nextCursor: data.next_cursor || null,
  };
}
